import { Button, Card, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Col, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setShowSendingPreviewModal, startLoading, stopLoading } from "../../store/slices/appSlice";
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { BarcodeDataToSaveDto, SaveBarcodesCommand } from "../../generated/tsClient";
import { fetchAnimalList, fetchSentBarcodes, saveBarcodes } from "../../services/homeService";
import { toast } from "react-toastify";
import { getAutoCloseTime } from "../../utils/common";
import { useEffect, useState } from "react";

function SendingPreviewModal(props: any) {
    const { t } = useTranslation(["texts", "errors"]);
    const dispatch = useAppDispatch();
    const showSendingPreviewModal = useAppSelector((state) => state.app.showSendingPreviewModal);
    const barcodeSendTo = useAppSelector((state) => state.app.selectedBarcodesSendTo);
    const isLoading = useAppSelector((state) => state.app.loading);
    const animalList = useAppSelector((state) => state.app.animalList);
    const barcodesSendToOptions = useAppSelector((state) => state.app.barcodesSendToOptions);
    const selectedHerd = useAppSelector((state) => state.app.selectedHerd);
    const sentBarcodes = useAppSelector((state) => state.app.sentBarcodes);
    const canOrderPregCheck = useAppSelector((state) => state.app.herdCanOrderPregCheck);
    const showPregCheckMessage = useAppSelector((state) => state.app.showPregCheckMessage);
    const [sentBarcodeCount, setSentBarcodeCount] = useState<number | null>(null);
    const [sentPregCheckOrderCount, setSentPregCheckOrderCount] = useState<number | null>(null);

    // NALI-284 show message for Arla producers
    const pregCheckCount = (animalList?.filter(a => a.pregCheckOrder).length ?? 0) + (sentPregCheckOrderCount ?? 0);
    const pregCheckMessage = showPregCheckMessage && pregCheckCount > 0
        ? t("Notification.ArlaPregTest").replace("{0}", pregCheckCount.toString())
        : "";
 
    useEffect(() => {
        const sendings = sentBarcodes?.filter(b => new Date(b.sentDate).toDateString() == (new Date).toDateString());
        var barcodeCount = 0;
        var pregCheckOrderCount = 0;
        sendings?.forEach(s => {
            barcodeCount += s.barcodeCount ?? 0;
            pregCheckOrderCount += s.pregCheckOrderCount ?? 0;
        });
        setSentBarcodeCount(barcodeCount);
        setSentPregCheckOrderCount(pregCheckOrderCount);
    }, [sentBarcodes]);

    const toggleSendingPreviewModal = () => {
        dispatch(setShowSendingPreviewModal(!showSendingPreviewModal));
    };

    const handleSendClick = () => {
        if (animalList != null) {
            const command = new SaveBarcodesCommand();
            command.herdId = selectedHerd?.idkar;
            command.sendTo = barcodeSendTo;
            command.barcodes = animalList.filter(a => a.barcode != null).map(a => new BarcodeDataToSaveDto({ bovineId: a.bovineId, barcode: a.barcode, pregCheckOrder: a.pregCheckOrder ?? false }));

            dispatch(startLoading());
            saveBarcodes(command)
                .then(() => {
                    toast.success(`${t("Notification.BarcodesSend")}`, {
                        autoClose: getAutoCloseTime(),
                    });

                    if (pregCheckMessage)
                        toast.warning(pregCheckMessage, {
                            autoClose: 10000,
                        });

                    localStorage.removeItem("mobilink_animalList_" + selectedHerd?.idkar);
                    localStorage.removeItem("mobilink_animalList_loaded_" + selectedHerd?.idkar);
                    if (selectedHerd?.idkar != null)
                        fetchAnimalList(selectedHerd.idkar);
                }).finally(() => {
                    if (selectedHerd?.idkar != null)
                        fetchSentBarcodes(selectedHerd.idkar);
                    dispatch(stopLoading());
                });
        }
    };
    
    return (
        <Modal
            isOpen={showSendingPreviewModal }
            toggle={toggleSendingPreviewModal}
            id="sending-preview-modal"
        >
            <ModalHeader
                toggle={toggleSendingPreviewModal}
            >
                <Row>
                    <Col><img width="60" src="./img/ProAgria_logo.png" alt="MobiLink" /></Col>
                </Row>
                <Row>
                    <Col>{t("ApplicationName")}</Col>
                </Row>
            </ModalHeader>
            <ModalBody>
                <Card>
                    {t("Summary. Title")}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>{t("Summary.Saved")}</TableCell>
                                <TableCell>{t("Summary.Sent") }</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>{t("Summary.Barcodes") }</TableCell>
                                <TableCell>{animalList?.filter(a => a.barcode != null).length }</TableCell>
                                <TableCell>{sentBarcodeCount }</TableCell>
                            </TableRow>
                            { canOrderPregCheck && (
                            <TableRow>
                                    <TableCell>{t("Summary.FertilityTests") + (pregCheckMessage ? "*" : "") }</TableCell>
                                    <TableCell>{animalList?.filter(a => a.pregCheckOrder).length}</TableCell>
                                    <TableCell>{sentPregCheckOrderCount }</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Row>
                        <Col>
                            {t("Summary.NoBarcodes")} {(animalList?.filter(a => a.barcode == null).length ?? 0) - (sentBarcodeCount ?? 0) }
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {t("Summary.BarcodesSendTo")} {barcodesSendToOptions?.find(o => o.paramCode === barcodeSendTo)?.name }
                        </Col>
                    </Row>
                    {barcodeSendTo === 2 && (<Row className="red"><Col>{t("Summary.Notification")}</Col></Row>)}
                    {pregCheckMessage && (<Row className="red"><Col>{pregCheckMessage}</Col></Row>)}
                    <Row>
                        <Col><Button variant="contained" color="secondary" onClick={toggleSendingPreviewModal }>{t("Summary.GoBack") }</Button></Col>
                        <Col>
                            <Button variant="contained" className="pull-right" onClick={handleSendClick} disabled={!animalList?.some(a => a.barcode != null)}>
                                {isLoading ? (
                                    <Spinner
                                        style={{ width: "1.5rem", height: "1.5rem" }}
                                        color="primary"
                                    />
                                ) : (<span><SendOutlinedIcon /> {t("Summary.Send")}</span>)}
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </ModalBody>
        </Modal>
    );
}

export default SendingPreviewModal;